import { useCallback, useMemo } from "react";
import { useDeviceType } from "@b2bportal/core-utilities";
import { useI18nContext } from "@hopper-b2b/i18n";
import { MonthAndDatePickerType } from "@tenant/ui";
import dayjs from "dayjs";
import { HotelsRawEventName, useEmitCoreHotelsRawEvent } from "../../tracking";
import type { DateRangePickerComponentProps } from "./DateRangePicker";

export const useDateRangePicker = ({
  startDate: stringStartDate,
  endDate: stringEndDate,
  onStartDateChangeHandler,
  onEndDateChangeHandler,
  onDoneClick,
  onClose,
  dialogTitle,
}: DateRangePickerComponentProps) => {
  const { t } = useI18nContext();

  const emitCalendarOpened = useEmitCoreHotelsRawEvent(
    HotelsRawEventName.HotelCalendarOpened
  );
  const emitCalendarDateChanged = useEmitCoreHotelsRawEvent(
    HotelsRawEventName.HotelCalendarDateChanged
  );
  const emitCalendarDateSelected = useEmitCoreHotelsRawEvent(
    HotelsRawEventName.HotelCalendarDatesSelected
  );

  const { isDesktopAndUp } = useDeviceType();
  // Use of `dayjs` below is necessary, since we don't have consistent date
  // handling, and the dates may show as previous day in some cases.
  const startDate = useMemo(() => {
    return stringStartDate != null ? dayjs(stringStartDate).toDate() : null;
  }, [stringStartDate]);

  const endDate = useMemo(() => {
    return stringEndDate != null ? dayjs(stringEndDate).toDate() : null;
  }, [stringEndDate]);

  const onStartDateChange = useCallback(
    (date: Date | null) => {
      onStartDateChangeHandler(date ? date.toString() : undefined);
      if (date) {
        emitCalendarDateChanged({
          properties: {
            startDate: date.toISOString(),
            endDate: endDate?.toISOString(),
          },
        });
      }
    },
    [onStartDateChangeHandler, endDate]
  );

  const onEndDateChange = useCallback(
    (date: Date | null) => {
      onEndDateChangeHandler(date ? date.toString() : undefined);
      if (date && startDate) {
        const eventProperties = {
          properties: {
            startDate: startDate.toISOString(),
            endDate: date.toISOString(),
          },
        };
        emitCalendarDateChanged(eventProperties);
      }
    },
    [onEndDateChangeHandler, startDate]
  );

  const onOpen = useCallback(() => {
    emitCalendarOpened({});
  }, []);

  const onDone = useCallback(() => {
    if (endDate && startDate) {
      const eventProperties = {
        properties: {
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
        },
      };
      emitCalendarDateSelected(eventProperties);
    }
    onDoneClick?.();
  }, [startDate, endDate, emitCalendarDateSelected, onDoneClick]);

  return {
    context: {
      stringStartDate,
      stringEndDate,
      startDate,
      endDate,
      dialogFullscreen: !isDesktopAndUp,
      calendarViewType: isDesktopAndUp
        ? MonthAndDatePickerType.Horizontal
        : MonthAndDatePickerType.Column,
    },
    handlers: {
      onDoneClick: onDone,
      onStartDateChange,
      onEndDateChange,
      onClose,
      onOpen,
    },
    text: {
      startDateLabel: t("core-hotels.search.date.startDate"),
      endDateLabel: t("core-hotels.search.date.endDate"),
      dialogDone: t("core-hotels.search.date.applyButton"),
      dialogTitle,
    },
  };
};
